import { useSelector, useDispatch } from 'react-redux';
import { BrandBadge } from '../../brands';
import { deleteQuery } from '../../store/knownDataSlice';
import RouteMap from '../../components/RouteMap';
import { displayDistance, displayPrice, displayRouteStops, formatDuration } from '../../tables/common';
import { toast } from 'react-toastify';
import { unwrapResult } from '@reduxjs/toolkit';
import { useNavigate } from 'react-router-dom';
import { fetchAll } from '../../store/shownDataSlice';
import { useModals } from '@mattjennings/react-modal-stack';
import RouteEditor from '../../components/RouteEditor';
import DriverConfirmationIndicator from '../../components/DriverConfirmationIndicator';
import { REJECTED_NAMES } from '../../tables/RejectedTable';
import { useState } from 'react';
import FNS from '../../api';
import JobLifecycle from './JobLifecycle';



function timeConverter(UNIX_timestamp){
    const d = new Date(UNIX_timestamp * 1000);
    return d.toLocaleDateString('et-EE') + ' ' + d.toLocaleTimeString('et-EE').substring(0, 5);
}

function driverConfirmationStatus(data, driverID) {
    const confirmations = data["driver_confirmations"] || [];
    const confirmation = confirmations.find((confirmation) => confirmation.driver_id === driverID);
    if(confirmation) return confirmation.status;
    // Jobs from before per-driver confirmations only have the job-level status,
    // which belongs to the primary driver
    if(driverID && driverID === data["job_driver"]) return data["job_confirmation_status"];
    return "waiting";
}

function QueryDetails({ id }) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const data = useSelector((state) => state.knownData[id]);
    const user = useSelector((state) => state.user.data);
    const { openModal } = useModals();
    const [isSending, setIsSending] = useState(false);
    const [rejectionReason, setRejectionReason] = useState("cancelled_via_platform");

    async function onDelete() {
        if(window.confirm("Kas oled kindel, et soovid kustutada?")) {
            const res = await unwrapResult(await dispatch(deleteQuery({ id })));
            if(!res) return;

            dispatch(fetchAll());
            toast.success("Vedu kustutatud!");
            navigate('/platform');
        }
    }

    function onEditRoute() {
        openModal(RouteEditor, { id });
    }

    function generateGoogleMapsURL(route) {
        const origin = route[0]["lat"] + "," + route[0]["lng"];
        const waypoints = route.slice(1, route.length - 1).map(route => route["lat"] + "," + route["lng"]);
        const destination = route[route.length - 1]["lat"] + "," + route[route.length - 1]["lng"];

        return `https://www.google.com/maps/dir/?api=1&origin=${origin}${waypoints.length && '&waypoints='+waypoints.join('%7C')}&destination=${destination}&travelmode=driving`;
    }


    async function acceptResponse() {
        if (isSending) return;
        setIsSending(true);

        try {
            // Use the existing data from the query for the response
            const response = await FNS.emailAcceptResponse(
                id,
                0, // activation_key of 0 for admin bypass
                data.query_fullname,
                data.query_email,
                data.query_phone
            );

            if (response.alreadyAnswered) {
                toast.warning("See päring on juba vastatud!");
            } else {
                toast.success("Päring vastu võetud!");
                navigate("/platform/confirmedQuery/" + id);
            }
        } catch (error) {
            console.error(error);
            toast.error("Viga päringu vastuvõtmisel!");
        } finally {
            setIsSending(false);
        }
    }

    async function rejectResponse() {
        if (isSending) return;
        setIsSending(true);

        try {
            const response = await FNS.emailRejectResponse(
                id,
                0, // activation_key of 0 for admin bypass
                rejectionReason
            );

            if (response.alreadyAnswered) {
                toast.warning("See päring on juba vastatud!");
            } else {
                toast.success("Päring tagasi lükatud!");
                navigate("/platform/answeredQuery/" + id + "#");
            }
        } catch (error) {
            console.error(error);
            toast.error("Viga päringu tagasilükkamisel!");
        } finally {
            setIsSending(false);
        }
    }

    if(!data) return;

    console.log("db30: data:", data);

    return (

        <div className="line g32">

            <div className="column g16 dont-shrink" style={{ width: 600, maxWidth: '100%' }}>

                <RouteMap job={data} />

                {data["query_driver_notes"] && (
                    <div className="column">
                        <b>Märkmed logistikult</b>
                        <p style={{ whiteSpace: 'pre-line' }}>{data["query_driver_notes"] || "-"}</p>
                    </div>
                )}

                <div className="column">
                    <b>Kliendi lisainfo</b>
                    <p>{data["query_notes"] || "-"}</p>
                </div>

                {data["query_response_id"] && (
                    <div className="column">
                        <b>"Pane tähele" tekst</b>
                        <p style={{ whiteSpace: 'pre-line' }}>{data["query_response_logistics_notes"] || "-"}</p>
                    </div>
                )}

                <div className="column">
                    <div className="row g16">
                        <b className='grow'>Marsruut</b>

                        {(user && user["role"] == "admin") ? (
                            <button onClick={onEditRoute}>Muuda</button>
                        ) : (
                            <button onClick={() => window.open(generateGoogleMapsURL(JSON.parse(data["query_route"])))}>Ava kaardil</button>
                        )}
                    </div>
                    <div className="column g8">
                        {JSON.parse(data["query_route"]).map((stop, index) => (
                            <div className="column">
                                <p key={index}>
                                    <b>{1+index}.</b> {stop["pretty_name"]}
                                </p>
                                {(!!stop.duration) && <b>
                                    Peatumine: {formatDuration(stop.duration)}
                                </b>}
                                <p style={{ color: '#888' }}>
                                    Koordinaadid: {stop["lat"]}, {stop["lng"]}
                                </p>
                                {(!!stop["arrive"]) && <p>
                                    Eeldatavalt kohal: {timeConverter(stop.arrive)}
                                </p>}
                                {(!!stop["depart"]) && <p>
                                    Väljumine: {timeConverter(stop.depart)}
                                </p>}
                            </div>
                        ))}
                    </div>
                </div>

            </div>

            <div className="column g8">
                {(data["job_sequential_id"]) && (
                    <div className="column">
                        <b>Töö nr. / Arve nr.</b>
                        <p>{data["job_sequential_id"]}</p>
                    </div>
                )}

                <div className="column">
                    <b>Sõitjaid</b>
                    <p>{data["query_passengers"]}</p>
                </div>

                { (!!data["query_start"]) && (
                    <div className="column">
                        <b>Start</b>
                        <p>{timeConverter(data["query_start"])}</p>
                    </div>
                ) }

                <div className="column">
                    <b>Teekond</b>
                    <p>{displayDistance(data)}</p>
                </div>

                <div className="column">
                    <b>Sõitmine</b>
                    <p>{formatDuration(data["query_duration"])}</p>
                </div>

                <div className="column">
                    <b>Peatumisi</b>
                    <p>{displayRouteStops(data)}</p>
                </div>

                { (user && user["role"] == "admin" && data["query_response_id"]) && (
                    <>
                        <div className="column">
                            <b>Maksumus</b>
                            <p>{displayPrice(data["query_response_price"])}</p>
                        </div>
                        <div className="column">
                            <b>Päringule vastas</b>
                            <p>{(data["query_responder_id"]) ? data["query_responder_firstname"] : "-"}</p>
                        </div>
                    </>
                )}

                { data["job_query_id"] && (
                    <>
                        <div className="column">
                            <b>Juht</b>
                            <div className="row g8">
                                <p>{data["driver_firstname"]} ({data["driver_username"]})</p>
                                <DriverConfirmationIndicator status={driverConfirmationStatus(data, data["job_driver"])} />
                            </div>
                            {data["extra_driver_names"] && data["extra_driver_names"].map((name, index) => (
                                <div className="row g8">
                                    <p>{name} ({data["extra_driver_usernames"][index]})</p>
                                    <DriverConfirmationIndicator status={driverConfirmationStatus(data, data["extra_driver_ids"] && data["extra_driver_ids"][index])} />
                                </div>
                            ))}
                        </div>

                        <div className="column">
                            <b>Buss</b>
                            <p>{data["bus_name"]}</p>
                            {data["extra_bus_names"] && data["extra_bus_names"].map((name, index) => (
                                <div className="row g8">
                                    <p>{name}</p>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                <div className="column">
                    <b>Tellija Täisnimi</b>
                    <p>{data["query_fullname"] || '-'}</p>
                </div>

                <div className="column">
                    <b>Tellija E-mail</b>
                    <p>{data["query_email"]}</p>
                </div>

                <div className="column">
                    <b>Tellija Telefon</b>
                    <p>{data["query_phone"]}</p>
                </div>

                {(data["query_group_fullname"]) && (
                    <>
                        <div className="column">
                            <b>Grupijuhi Täisnimi</b>
                            <p>{data["query_group_fullname"] || '-'}</p>
                        </div>

                        <div className="column">
                            <b>Grupijuhi E-mail</b>
                            <p>{data["query_group_email"]}</p>
                        </div>

                        <div className="column">
                            <b>Grupijuhi Telefon</b>
                            <p>{data["query_group_phone"]}</p>
                        </div>
                    </>
                )}

                <div className="column">
                    <b>Firma</b>
                    <p>{data["query_company"] || '-'}</p>
                </div>

                <div className="column">
                    <b>Bränd</b>
                    <p><BrandBadge brand={data["query_brand"]} /></p>
                </div>

                <div className="column">
                    <b>Päringu kuupäev</b>
                    <p>{timeConverter(data["query_created"])}</p>
                </div>

                <div className="column">
                    <b>Sooduskood</b>
                    <p>{data["query_promo"] || '-'}</p>
                </div>

                {(user && user["role"] == "admin") &&
                        // Hinnapakkumine saadetud, aga pole vastatud
                        ((data["query_response_id"] && data["query_response_status"] !== "accepted" && !data["job_query_id"])
                        // Kui töö alustatud, aga klient muudab meelt
                        || (data["job_query_id"])) && (

                    <div className="column g8">
                        {!data["job_query_id"] && (
                            <button
                                style={{ backgroundColor: '#8c8', color: 'white', fontWeight: 'bold' }}
                                onClick={acceptResponse}
                                disabled={isSending}
                            >
                                Võta vastu
                            </button>
                        )}

                        <div className="column g8"  style={{ border: '1px solid #ddd', padding: 8, borderRadius: 6 }}>

                            <select
                                value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)}
                                style={{ width: 'min-width' }}
                            >
                                {Object.entries(REJECTED_NAMES).map(([key, value]) => (
                                    <option key={key} value={key}>{value}</option>
                                ))}
                            </select>

                            <button
                                style={{ backgroundColor: '#c88', color: 'white', fontWeight: 'bold' }}
                                onClick={rejectResponse}
                                disabled={isSending}
                            >
                                Lükka tagasi
                            </button>

                        </div>
                    </div>

                )}

                {(user && user["role"] == "admin") && (
                    <button style={{ backgroundColor: '#d4635b', borderColor: '#f4837b', color: '#fff' }} onClick={onDelete} >Kustuta</button>
                )}

                {(user && user["role"] == "admin") && (
                    <JobLifecycle id={id} />
                )}
            </div>

        </div>
    );
};

export default QueryDetails;